import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import EmailComposer from "./EmailComposer";
import useAccess from "../hooks/useAccess";
import "../styles/UserActivities.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const formatActivityDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export function AlertActivityPanel({ user, onSent }) {
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [pendingTaskIds, setPendingTaskIds] = useState(() => new Set());

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFeedback(null);
    axios
      .get(`${API_BASE}/api/activities/users/${user.id}/tasks`, {
        headers: authHeaders(),
      })
      .then(({ data }) => {
        if (!active) return;
        setTaskOptions(
          (Array.isArray(data) ? data : []).map((task) => ({
            value: Number(task.task_id),
            label: task.title,
            categoryId: task.category_id,
            categoryName: task.category_name || "Other",
            categoryOrder: Number(task.category_order || 9999),
            taskOrder: Number(task.task_order || 9999),
          })),
        );
      })
      .catch((error) => {
        if (active) {
          setFeedback({
            type: "error",
            text:
              error.response?.data?.message || "Unable to load assigned tasks.",
          });
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [user.id]);

  const sendAlert = async () => {
    if (!selectedTasks.length || sending) return;
    setSending(true);
    setFeedback(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/activities/alerts`,
        {
          userId: user.id,
          taskIds: selectedTasks.map((task) => task.value),
          message,
        },
        { headers: authHeaders() },
      );
      setSelectedTasks([]);
      setMessage("");
      setFeedback({
        type: "success",
        text: data.message || "Alert sent successfully.",
      });
      onSent?.();
    } catch (error) {
      setFeedback({
        type: "error",
        text: error.response?.data?.message || "The alert could not be sent.",
      });
    } finally {
      setSending(false);
    }
  };

  const recipientName =
    `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const groupedTasks = useMemo(() => {
    const groups = new Map();
    taskOptions.forEach((task) => {
      const key = `${task.categoryId ?? "other"}:${task.categoryName}`;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: task.categoryName,
          order: task.categoryOrder,
          tasks: [],
        });
      }
      groups.get(key).tasks.push(task);
    });
    return [...groups.values()].sort(
      (left, right) =>
        left.order - right.order || left.name.localeCompare(right.name),
    );
  }, [taskOptions]);

  const openTaskSelector = () => {
    setPendingTaskIds(new Set(selectedTasks.map((task) => task.value)));
    setSelectorOpen(true);
  };

  const togglePendingTask = (taskId) => {
    setPendingTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleAllTasks = () => {
    setPendingTaskIds((current) =>
      current.size === taskOptions.length
        ? new Set()
        : new Set(taskOptions.map((task) => task.value)),
    );
  };

  const saveTaskSelection = () => {
    setSelectedTasks(
      taskOptions.filter((task) => pendingTaskIds.has(task.value)),
    );
    setSelectorOpen(false);
  };

  return (
    <>
      <section className="activity-compose-card">
        <div className="activity-compose-heading">
          <i className="fa fa-exclamation-circle" aria-hidden="true" />
          <h2>Alert Message and Requested Documents</h2>
        </div>
        <div className="activity-compose-body">
          <div className="activity-form-row">
            <label>Recipient</label>
            <div className="activity-recipient">
              <strong>{recipientName}</strong>
              {user.email ? <span>{user.email}</span> : null}
            </div>
          </div>
          <div className="activity-form-row activity-form-row-top">
            <label>Documents Requested</label>
            <div className="activity-document-picker">
              <div
                className={`activity-selected-documents ${selectedTasks.length ? "has-selection" : ""}`}
              >
                {selectedTasks.length ? (
                  selectedTasks.map((task) => (
                    <span className="activity-document-chip" key={task.value}>
                      <span title={task.label}>{task.label}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${task.label}`}
                        onClick={() =>
                          setSelectedTasks((current) =>
                            current.filter((item) => item.value !== task.value),
                          )
                        }
                        disabled={sending}
                      >
                        &times;
                      </button>
                    </span>
                  ))
                ) : (
                  <span>No documents selected</span>
                )}
              </div>
              <button
                type="button"
                className="activity-choose-documents-btn"
                onClick={openTaskSelector}
                disabled={loading || sending || !taskOptions.length}
              >
                <i className="fa fa-folder-open" aria-hidden="true" />
                {loading
                  ? "Loading Assigned Tasks..."
                  : "Choose Documents to Request"}
              </button>
              <small>Only tasks assigned to this user are available.</small>
            </div>
          </div>
          <div className="activity-form-row activity-form-row-top">
            <label htmlFor="activity-message">Message</label>
            <textarea
              id="activity-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Enter a message for this user"
              rows={6}
              disabled={sending}
            />
          </div>
          {feedback ? (
            <div className={`activity-feedback ${feedback.type}`} role="status">
              {feedback.text}
            </div>
          ) : null}
          <div className="activity-compose-actions">
            <button
              type="button"
              className="activity-send-btn"
              onClick={sendAlert}
              disabled={!selectedTasks.length || sending}
            >
              <span>{sending ? "Sending..." : "Send"}</span>
              <i className="fa fa-paper-plane" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {selectorOpen ? (
        <div className="activity-document-modal" role="presentation">
          <div
            className="activity-document-modal-backdrop"
            onClick={() => setSelectorOpen(false)}
          />
          <section
            className="activity-document-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="activity-document-dialog-title"
          >
            <header>
              <h2 id="activity-document-dialog-title">
                Select Document Types for Alert
              </h2>
              <button
                type="button"
                className="app-window-close activity-document-close"
                data-no-standard-button="true"
                onClick={() => setSelectorOpen(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>
            <div className="activity-document-dialog-body">
              <label className="activity-select-all">
                <input
                  type="checkbox"
                  checked={
                    taskOptions.length > 0 &&
                    pendingTaskIds.size === taskOptions.length
                  }
                  onChange={toggleAllTasks}
                />
                <span>Select All</span>
              </label>
              {groupedTasks.map((group) => (
                <section className="activity-document-group" key={group.key}>
                  <h3>{group.name}</h3>
                  <div className="activity-document-grid">
                    {group.tasks.map((task) => (
                      <label key={task.value}>
                        <input
                          type="checkbox"
                          checked={pendingTaskIds.has(task.value)}
                          onChange={() => togglePendingTask(task.value)}
                        />
                        <span>{task.label}</span>
                      </label>
                    ))}
                  </div>
                </section>
              ))}
            </div>
            <footer>
              <span>{pendingTaskIds.size} selected</span>
              <div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSelectorOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveTaskSelection}
                >
                  Save
                  <i className="fa fa-save" aria-hidden="true" />
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}

export function EmailActivityPlaceholder({ user, onSent }) {
  return <EmailComposer initialUser={user} onSent={onSent} />;
}

export default function UserActivities({
  userId,
  refreshKey,
  onOpenAlert,
  onOpenEmail,
}) {
  const { hasPermission, permissionsLoaded } = useAccess();
  const canViewAlerts = hasPermission("Communications", "Alerts", "view");
  const canEditAlerts = hasPermission("Communications", "Alerts", "edit");
  const canViewEmail = hasPermission("Communications", "Email", "view");
  const canEditEmail = hasPermission("Communications", "Email", "edit");
  const [filter, setFilter] = useState("all");
  const [activities, setActivities] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadActivities = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/activities/users/${userId}`,
        {
          params: { type: filter },
          headers: authHeaders(),
        },
      );
      setActivities(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message || "Unable to load activities.",
      );
    } finally {
      setLoading(false);
    }
  }, [filter, userId]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities, refreshKey]);

  const toggleExpanded = (id) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filters = useMemo(
    () => [
      { key: "all", label: "All" },
      ...(canViewAlerts ? [{ key: "alert", label: "Alerts" }] : []),
      ...(canViewEmail ? [{ key: "email", label: "Emails" }] : []),
    ],
    [canViewAlerts, canViewEmail],
  );

  const visibleActivities = useMemo(
    () =>
      activities.filter(
        (activity) =>
          (activity.type === "alert" && canViewAlerts) ||
          (activity.type === "email" && canViewEmail),
      ),
    [activities, canViewAlerts, canViewEmail],
  );

  return (
    <section className="user-activities-card">
      <div className="user-activities-header">
        <h2>Activities</h2>
        <div className="activity-quick-actions" aria-label="Create activity">
          {permissionsLoaded && canEditAlerts ? (
            <button type="button" onClick={onOpenAlert} aria-label="Send alert">
              <i className="fa fa-exclamation-circle" aria-hidden="true" />
            </button>
          ) : null}
          {permissionsLoaded && canEditEmail ? (
            <button type="button" onClick={onOpenEmail} aria-label="Email">
              <i className="fa fa-envelope" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
      <div
        className="activity-filters"
        role="group"
        aria-label="Filter activities"
      >
        {filters.map((item) => (
          <button
            type="button"
            key={item.key}
            className={filter === item.key ? "active" : ""}
            onClick={() => setFilter(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="activity-list">
        {loading ? (
          <div className="activity-empty">Loading activities...</div>
        ) : null}
        {!loading && error ? (
          <div className="activity-empty error">{error}</div>
        ) : null}
        {!loading && !error && !visibleActivities.length ? (
          <div className="activity-empty">No activities found.</div>
        ) : null}
        {!loading && !error
          ? visibleActivities.map((activity) => {
              const isOpen = expanded.has(activity.id);
              const documents = Array.isArray(activity.metadata?.documents)
                ? activity.metadata.documents
                : [];
              const isAlert = activity.type === "alert";
              return (
                <article className="activity-history-item" key={activity.id}>
                  <div className="activity-type-icon">
                    <i
                      className={`fa ${isAlert ? "fa-exclamation-circle" : "fa-envelope"}`}
                      aria-hidden="true"
                    />
                  </div>
                  <button
                    type="button"
                    className="activity-history-summary"
                    onClick={() => toggleExpanded(activity.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="activity-history-main">
                      <strong>{isAlert ? "Alert" : "Email"}</strong>
                      <time>{formatActivityDate(activity.created_at)}</time>
                      <span className="activity-author">
                        <i className="fa fa-user" aria-hidden="true" />
                        {activity.created_by_name || "Administrator"}
                      </span>
                    </span>
                    <span className="activity-history-detail">
                      <b>{isAlert ? "Documents:" : "Subject:"}</b>{" "}
                      {isAlert
                        ? documents
                            .map((document) => document.title)
                            .join(", ") || "None"
                        : activity.subject || ""}
                    </span>
                    {isOpen ? (
                      <span className="activity-expanded-message">
                        <b>Message:</b>
                        <span>
                          {activity.message || "No message was included."}
                        </span>
                      </span>
                    ) : null}
                    <i
                      className={`fa fa-chevron-${isOpen ? "up" : "down"} activity-chevron`}
                      aria-hidden="true"
                    />
                  </button>
                </article>
              );
            })
          : null}
      </div>
    </section>
  );
}
